import React from "react";
import { motion } from "framer-motion";
import { ChefHat, Heart, Award, Star, Utensils, Sparkles } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { restaurantInfo } from "@/data/index";
import { FeatureCard } from "@/components/Cards";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

const valueIcons: Record<string, React.ReactNode> = {
  "Authentic Flavors": <Star className="w-6 h-6" />,
  "Specialized Cuisine": <ChefHat className="w-6 h-6" />,
  "Reasonable Pricing": <Award className="w-6 h-6" />,
  "Owner-Operated": <Heart className="w-6 h-6" />,
};

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden bg-secondary/30">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
          <Sparkles className="w-full h-full text-primary" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-semibold tracking-wider uppercase bg-primary/10 text-primary rounded-full">
              Our Story
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              {restaurantInfo.name}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground font-medium italic">
              "{restaurantInfo.tagline}"
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Story Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl border-8 border-white dark:border-card">
                <img 
                  src={IMAGES.RESTAURANT_INTERIOR_2} 
                  alt="Modern Pink Interior" 
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-primary rounded-full flex items-center justify-center text-primary-foreground shadow-xl hidden md:flex">
                <div className="text-center">
                  <Utensils className="w-10 h-10 mx-auto mb-2" />
                  <span className="font-bold text-sm uppercase tracking-widest">Since 2024</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="space-y-8"
            >
              <h2 className="text-4xl font-bold text-foreground">Authenticity in Every Bite</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {restaurantInfo.description}
              </p>
              <div className="p-8 bg-accent/50 rounded-2xl border border-accent">
                <h3 className="text-xl font-bold text-accent-foreground mb-4 flex items-center gap-2">
                  <ChefHat className="w-6 h-6 text-primary" />
                  The Hands-On Approach
                </h3>
                <p className="text-accent-foreground/80">
                  Unlike chain restaurants, Bagus Chicken Dakgalbi is owner-operated. This means every spice mix is personally tasted, every chicken cut is inspected, and every customer's feedback goes directly to the heart of our leadership. It's this hands-on passion that makes our food "Bagus" (Good).
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Defines Us</h2>
            <p className="text-muted-foreground">
              We believe in more than just serving food; we believe in creating an experience that stays with you.
            </p>
          </div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {restaurantInfo.values.map((value) => (
              <motion.div key={value.title} variants={staggerItem}>
                <FeatureCard 
                  title={value.title}
                  description={value.description}
                  icon={valueIcons[value.title] || <Star className="w-6 h-6" />}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Culinary Philosophy Section */}
      <section className="py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row-reverse gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={springPresets.bouncy}
              className="flex-1"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
                <img 
                  src={IMAGES.FOOD_PRESENTATION_7} 
                  alt="Spicy Tteokbokki"
                  className="relative z-10 w-full max-w-md mx-auto rounded-3xl shadow-2xl rotate-3 hover:rotate-0 transition-transform duration-500"
                />
              </div>
            </motion.div>

            <div className="flex-1 space-y-8">
              <h2 className="text-4xl font-bold">A Pink-Themed Oasis</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Why pink? Because we wanted to break the mold of traditional dark Korean BBQ interiors. Our modern, clean, and friendly pink-themed branding is designed to make you feel at home, whether you're here for a quick lunch or a celebratory dinner with friends.
              </p>
              <ul className="space-y-4">
                {[ 
                  "Modern, Instagram-ready aesthetics",
                  "Clean and spacious dining environment",
                  "Friendly, attentive service staff",
                  "Centrally located in Sunway Geo Mall"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground font-medium">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="bg-primary rounded-[2.5rem] p-12 md:p-20 text-center text-primary-foreground relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-black/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative z-10 max-w-2xl mx-auto"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to taste the authenticity?</h2>
              <p className="text-xl text-primary-foreground/80 mb-10">
                Join us at Sunway Geo Mall or have our signature Dakgalbi delivered straight to your door.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="/menu" 
                  className="px-8 py-4 bg-white text-primary font-bold rounded-xl hover:bg-opacity-90 transition-all hover:scale-105 shadow-lg"
                >
                  Explore Our Menu
                </a>
                <a 
                  href="/location" 
                  className="px-8 py-4 bg-primary-foreground/10 border border-white/20 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
                >
                  Visit Us Today
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
